/***
 * Created on 2023/7/10
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useRef, useState} from 'react';
import PreferLanguage from "./prefer_language";
import Country from "./country";
import Timezone from "./timezone";
import {QueryClient, useMutation, useQuery} from "@tanstack/react-query";
import {apiGet, apiPost} from "../../service/api";
import Qid from "./qid";
import AvatarUploadModal from "./avatar_upload_modal";
import TextInput from "../common/form/text_input";
import {useBoundedStore} from 'common/Store';
import SwitchInput from "../common/form/switch_input";
import {useTranslation} from "react-i18next";
import SocialLinkage from "./social_linkage";

const queryClient = new QueryClient();

function UserSettings(props) {
    const { t } = useTranslation();
    const store = useBoundedStore();
    const [showUpload, setShowUpload] = useState(false);
    const [showAvatarOption, setShowAvatarOption] = useState('');
    const avatarRef = useRef();
    const [hasInvalidInputs, setHasInvalidInputs] = useState()

    const [inputValues, setInputValues] = useState({
        first_name: '',
        last_name: '',
        display_name: '',
        country: '',
        language: '',
        timezone: ''
    });
    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log(inputValues);
        hasInvalidInputs === 0 && profileMutation.mutate(inputValues);
    };


    const user = useQuery({
        queryKey: ['profile'],
        queryFn: async () => {
            const resp = await apiGet('/ajax/profile');
            setInputValues(() => {
                return {
                    first_name: resp?.first_name,
                    last_name: resp?.last_name,
                    display_name: resp?.display_name,
                    country: resp?.country,
                    language: resp?.language,
                    timezone: resp?.timezone
                }
            })
            localStorage.setItem('currentUser', JSON.stringify(resp));
            return resp;
        },
        refetchOnWindowFocus: false,
        // staleTime: 60 * 60 * 1000   // re-fetch the data in 1 hour
    })

    const profileMutation = useMutation({
        mutationFn: (data) => apiPost(`/ajax/profile`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['profile']
            });
            store.globalAlert.setAlert(t('@Your profile is updated.@'), 'success', true);
        },
        onError: (error, variables, context) => {
            if (error?.cause) {
                console.log(error.cause);
                store.globalAlert.setAlert(error.cause.message, 'error', true);
            }
            else {
                console.log(error);
                store.globalAlert.setAlert(error.message, 'error', true);
            }
        }
    })

    const hideAvatarOptionHandler = (e) => {
        if (avatarRef.current && !avatarRef.current.contains(e.target)) {
            setShowAvatarOption('');
        }
    }

    const toggleAvatarOption = (e) => {
        setShowAvatarOption(prev => {
            return (prev === 'active') ? '' : 'active';
        });
    }

    const onChange = (e, callback, name=null, value=null) => {
        if (callback && typeof callback === 'function') {
            callback(e);
        }
        const _name = name ? name : e.target.name;
        const _value = value ? value: e.target.value;
        setInputValues(prev => {
            return {
                ...prev,
                [_name]: _value
            }
        })
    }

    const handleAvatar404 = (e, name) => {
        const parent_element = e.target.parentElement;
        const new_child = document.createElement('span');
        new_child.textContent = name;
        parent_element.appendChild(new_child);
        e.target.remove();
    }

    useEffect(() => {
        window.addEventListener('click', hideAvatarOptionHandler);

        return () => {
            window.removeEventListener('click', hideAvatarOptionHandler);
        }
    });

    useEffect(() => {
        const invalidInputs = document.querySelectorAll('input:invalid');
        setHasInvalidInputs(invalidInputs?.length || 0);
    });

    return (
        <>
            <div className="page-content page-content--account">
                <div className="account--col-left">
                    <div className="user--avatar">
                        <div className="comp-avatar no-avatar">
                            {
                                user?.data?.avatars?.medium && user?.data?.avatars?.medium?.length > 0 &&
                                <img src={user.data.avatars.medium} alt="avatar" onError={e => handleAvatar404(e, user?.data?.display_name[0])}/> ||
                                <span>{user?.data?.display_name[0]}</span>
                            }
                        </div>
                    </div>
                    <div className="position-relative">
                        <div className="user--name">{user?.data?.display_name}</div>
                        <div className="user--upload-image">
                            <div className={`comp-select ${showAvatarOption}`} onClick={toggleAvatarOption} ref={avatarRef}>
                                <div className="q-select cursor-pointer">
                                    <div className="q-select__label">{t('@Upload photo@')}</div>
                                    <div className="q-select__arrow"></div>
                                </div>

                                    <div className="comp-dropdown" data-modal-sheet>
                                        <ul className="dropdown-menu">
                                            <li className="dropdown-menu__item cursor-pointer"
                                                onClick={() => setShowUpload(true)}>{t('@Upload photo@')}</li>
                                            { user?.data?.linked?.facebook &&
                                                <li className="dropdown-menu__item cursor-pointer">{t('@Sync Facebook avatar@')}</li>
                                            }
                                        </ul>
                                    </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="account--col-right">
                    <div className="forms">
                        <Qid emails={user?.data?.emails} phones={user?.data?.phones}/>
                        <TextInput {...{placeholder:t('@First Name@'), name: "first_name", label:t('@First Name@'), value: inputValues.first_name, onChange}} />
                        <TextInput {...{placeholder:t('@Last Name@'), name: "last_name", label:t('@Last Name@'), value: inputValues.last_name, onChange}} />
                        <TextInput {...{placeholder:t('@Display Name@'), name: "display_name", required:true, label:t('@Display Name@'), value: inputValues.display_name, onChange}} />
                        <Country initValue={inputValues?.country} onClick={onChange} />
                        <PreferLanguage initValue={inputValues?.language} onClick={onChange}/>
                        <Timezone initValue={inputValues?.timezone?.listName} onClick={onChange}/>
                        <SocialLinkage user={user?.data} />
                    </div>
                </div>
                <div className="account--action ">
                    <button type="button" className="q-btn q-btn--primary" onClick={handleSubmit} disabled={hasInvalidInputs > 0}>{t('@Update Profile@')}</button>
                </div>
            </div>
            {
                showUpload && <AvatarUploadModal showModal={showUpload} closeModal={()=>setShowUpload(false)}/>
            }
        </>
    );
}

export default UserSettings;